import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type CartItem = {
  // Unique line key (product + sku combo)
  lineId: string;
  productId: number;
  productName: string;
  image: string;
  skuId: number | null;
  skuLabel: string | null;
  unitPrice: number;
  finalPrice: number;
  quantity: number;
  maxStock: number;
  currency: string;
};

export type AddToCartPayload = Omit<CartItem, "lineId">;

type CartContextValue = {
  items: CartItem[];
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  addItem: (payload: AddToCartPayload) => void;
  removeItem: (lineId: string) => void;
  updateQuantity: (lineId: string, quantity: number) => void;
  clear: () => void;
  totalCount: number;
  subtotal: number;
};

const CartContext = createContext<CartContextValue | null>(null);

const STORAGE_KEY = "hearth-cart-v1";

const lineKey = (productId: number, skuId: number | null) =>
  `${productId}::${skuId ?? "default"}`;

const loadFromStorage = (): CartItem[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (it): it is CartItem =>
        typeof it === "object" &&
        it !== null &&
        typeof it.lineId === "string" &&
        typeof it.productId === "number" &&
        typeof it.quantity === "number",
    );
  } catch {
    return [];
  }
};

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => loadFromStorage());
  const [isOpen, setIsOpen] = useState(false);

  // Persist to localStorage on change
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* storage may be full or blocked, fail quietly */
    }
  }, [items]);

  // Stay in sync if another tab updates the cart
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setItems(loadFromStorage());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Lock body scroll while the drawer is open
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isOpen]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((s) => !s), []);

  const addItem = useCallback((payload: AddToCartPayload) => {
    const id = lineKey(payload.productId, payload.skuId);
    setItems((prev) => {
      const existing = prev.find((it) => it.lineId === id);
      if (existing) {
        const nextQty = Math.min(
          existing.maxStock || 99,
          existing.quantity + payload.quantity,
        );
        return prev.map((it) =>
          it.lineId === id
            ? {
                ...it,
                quantity: nextQty,
                // Keep the freshest price/stock snapshots
                unitPrice: payload.unitPrice,
                finalPrice: payload.finalPrice,
                maxStock: payload.maxStock,
                image: payload.image || it.image,
              }
            : it,
        );
      }
      return [...prev, { ...payload, lineId: id }];
    });
  }, []);

  const removeItem = useCallback((lineId: string) => {
    setItems((prev) => prev.filter((it) => it.lineId !== lineId));
  }, []);

  const updateQuantity = useCallback((lineId: string, quantity: number) => {
    setItems((prev) =>
      prev
        .map((it) =>
          it.lineId === lineId
            ? {
                ...it,
                quantity: Math.max(
                  1,
                  Math.min(quantity, it.maxStock || 99),
                ),
              }
            : it,
        )
        .filter((it) => it.quantity > 0),
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const totalCount = useMemo(
    () => items.reduce((sum, it) => sum + it.quantity, 0),
    [items],
  );

  const subtotal = useMemo(
    () => items.reduce((sum, it) => sum + it.finalPrice * it.quantity, 0),
    [items],
  );

  const value = useMemo<CartContextValue>(
    () => ({
      items,
      isOpen,
      open,
      close,
      toggle,
      addItem,
      removeItem,
      updateQuantity,
      clear,
      totalCount,
      subtotal,
    }),
    [items, isOpen, open, close, toggle, addItem, removeItem, updateQuantity, clear, totalCount, subtotal],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
}